import React from "react";
import { Box, Typography } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ImageMetadata } from "../types";

interface GuidingErrorVsHfrChartProps {
  imagesForView: ImageMetadata[];
}

const GuidingErrorVsHfrChart: React.FC<GuidingErrorVsHfrChartProps> = ({
  imagesForView,
}) => {
  // Only use light frames for statistics
  const lightFrames = imagesForView.filter((img) => img.FrameType === "LIGHT");
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: 3,
        p: 3,
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        minHeight: 400,
      }}
    >
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}
      >
        Guiding Error vs HFR
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Effect of guiding errors on star size
      </Typography>
      {lightFrames.length === 0 ? (
        <Typography color="text.secondary">No data available.</Typography>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart
            data={lightFrames
              .map((img, i) => ({
                frame: i + 1,
                rms: img.GuidingRMSArcSec,
                hfr: img.HFR,
              }))
              .filter(
                (d) =>
                  typeof d.hfr === "number" &&
                  isFinite(d.hfr) &&
                  typeof d.rms === "number" &&
                  isFinite(d.rms)
              )}
          >
            <XAxis
              dataKey="frame"
              stroke="#a0a0a0"
              tick={{ fill: "#a0a0a0" }}
              tickLine={false}
              axisLine={{ stroke: "#333" }}
            />
            <YAxis
              yAxisId="left"
              stroke="#4fc3f7"
              tick={{ fill: "#4fc3f7" }}
              tickLine={false}
              axisLine={{ stroke: "#333" }}
              domain={[0, "auto"]}
              label={{
                value: "Guiding Error",
                angle: -90,
                position: "insideLeft",
                fill: "#4fc3f7",
              }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="#ff8a65"
              tick={{ fill: "#ff8a65" }}
              tickLine={false}
              axisLine={{ stroke: "#333" }}
              domain={[0, "auto"]}
              label={{
                value: "HFR",
                angle: 90,
                position: "insideRight",
                fill: "#ff8a65",
              }}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || !payload.length) return null;
                const rms = payload.find((p) => p.dataKey === "rms");
                const hfr = payload.find((p) => p.dataKey === "hfr");
                return (
                  <div
                    style={{
                      background: "#222",
                      border: "1px solid #444",
                      color: "#fff",
                      padding: 8,
                      borderRadius: 4,
                    }}
                  >
                    <div>Frame: {label}</div>
                    {rms && (
                      <div>
                        Guiding Error:{" "}
                        {typeof rms.value === "number"
                          ? Number.isInteger(rms.value)
                            ? rms.value
                            : rms.value.toFixed(2)
                          : rms.value}
                      </div>
                    )}
                    {hfr && (
                      <div>
                        HFR:{" "}
                        {typeof hfr.value === "number"
                          ? Number.isInteger(hfr.value)
                            ? hfr.value
                            : hfr.value.toFixed(2)
                          : hfr.value}
                      </div>
                    )}
                  </div>
                );
              }}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="rms"
              stroke="#4fc3f7"
              strokeWidth={2}
              dot={false}
              name="Guiding Error"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="hfr"
              stroke="#ff8a65"
              strokeWidth={2}
              dot={false}
              name="HFR"
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export default GuidingErrorVsHfrChart;
